import PropTypes from 'prop-types'
import React from 'react'
import useSWR from 'swr'
import {Skeleton, Text, Button} from '@chakra-ui/react'
import {useNavigate, useLocation} from 'react-router'
import {useTranslation} from 'react-i18next'

import {NewSupplierLayout as SupplierLayout} from '../../layouts/SupplierLayout'
import {useRails} from '../../contexts/rails'
import withProviders from '../../contexts/withProviders'
import withSWR from '../../contexts/withSWR'
import {fetcher} from '../../../api'
import {camelizeResult} from '../../../api/serializer'
import CustomerDetails from './CustomerDetails'
import Statements from './Statements'
import UnpaidInvoices from './UnpaidInvoices'
import {NextScreenHeader as ScreenHeader} from '../../components/ScreenHeader'
import NotificationBanner from '../../components/NotificationBanner'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import {SectionContainer} from '../../components/Section'

export const SupplierCustomerScreen = ({customerId}) => {
  const {t} = useTranslation('customers')
  const navigate = useNavigate()
  const location = useLocation()
  const {organisation, authenticityToken} = useRails()

  const {data: featureFlagsData, loading: loadingFeatures} = useFeatureFlags()
  const features = featureFlagsData?.features

  const [bannerDismissed, setBannerDismissed] = React.useState(false)

  const {data, error, isLoading} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/customers/${customerId}`,
    fetcher,
    {
      use: [camelizeResult],
    },
  )

  const {data: stepperData, isLoading: loadingStepper} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/cashin_stepper`,
    fetcher,
    {
      use: [camelizeResult],
    },
  )

  const loading = loadingFeatures || loadingStepper

  const displayBankingDetailsNotification =
    !loading &&
    features?.invitePayersToMarmalade &&
    stepperData?.stepper?.canTransitionPayersToMarmalade &&
    !bannerDismissed

  if (error && error.message === 'Unauthorized') {
    window.location.href = '/sign_in'
  }

  if (error) {
    return (
      <Text ml="md">Unable to fetch customer, please try again later.</Text>
    )
  }

  if (isLoading) {
    return <Skeleton height="220px" />
  }

  const {customer} = data

  // The notification is an email to this customer, so there is nothing to offer
  // when we hold no address for them.
  const canNotifyCustomer = Boolean(customer?.email?.trim())

  const isNotified =
    customer?.bankingDetailsTransitionStatus?.status === 'notified'
  const notificationMessage = isNotified
    ? t('BankingDetailsNotification.customer.messageNotified', {
        customerName: customer.contactName,
        date: new Date(
          customer.bankingDetailsTransitionStatus.lastNotifiedAt,
        ).toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
      })
    : t('BankingDetailsNotification.customer.messageNotNotified', {
        customerName: customer.contactName,
      })

  return (
    <SupplierLayout state="Customers" variant="secondary">
      <ScreenHeader text={customer?.contactName} />
      {displayBankingDetailsNotification && canNotifyCustomer && (
        <SectionContainer>
          <NotificationBanner
            status="info"
            title={t('BankingDetailsNotification.customer.title')}
            message={notificationMessage}
            onClose={() => setBannerDismissed(true)}
            ctaButton={
              <Button
                className="fs-unmask"
                variant="primary"
                onClick={() => {
                  navigate(
                    '/customers/transition_to_marmalade/customize_email_template',
                    {
                      state: {
                        selectedCustomers: [customer],
                        referrer: location.pathname,
                      },
                    },
                  )
                }}
              >
                {t('BankingDetailsNotification.customer.button')}
              </Button>
            }
          />
        </SectionContainer>
      )}
      <CustomerDetails customer={customer} />
      <Statements
        latestStatement={customer.latestStatement}
        supplierId={organisation.currentSupplier.id}
        customerId={customer.id}
        customerEmail={customer.email}
        customerName={customer.contactName}
        authenticityToken={authenticityToken}
      />
      <UnpaidInvoices customerId={customerId} />
    </SupplierLayout>
  )
}

SupplierCustomerScreen.propTypes = {
  customerId: PropTypes.string.isRequired,
}

export default withProviders(withSWR(SupplierCustomerScreen))
